/**
 * Store de notifications
 * Gère les notifications et messages de l'application
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { apiFetch } from '@/services/api'

// Normalise une ligne API (is_read) vers le modèle du store (read).
function mapRow(n) {
  return {
    id: n.id,
    title: n.title,
    message: n.message,
    type: n.type,
    read: n.is_read ?? n.read ?? false,
    action_url: n.action_url || null,
    action_label: n.action_label || null,
    metadata: n.metadata || null,
    createdAt: n.created_at || n.createdAt || null,
  }
}

export const useNotificationsStore = defineStore('notifications', () => {
  // ============ STATE ============
  
  const notifications = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  
  // ============ GETTERS ============
  
  const unreadCount = computed(() => 
    notifications.value.filter(n => !n.read).length
  )
  
  const unreadNotifications = computed(() => 
    notifications.value.filter(n => !n.read)
  )
  
  const readNotifications = computed(() => 
    notifications.value.filter(n => n.read)
  )
  
  // ============ ACTIONS ============
  
  /**
   * Charge les notifications depuis l'API
   */
  const fetchNotifications = async () => {
    try {
      isLoading.value = true
      error.value = null
      const resp = await apiFetch('/api/private/notifications?limit=30')
      const data = await resp.json().catch(() => null)
      if (resp.ok && Array.isArray(data?.data)) {
        notifications.value = data.data.map(mapRow)
      }
      return { success: true }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Marque une notification comme lue (optimiste + API).
   */
  const markAsRead = async (notificationId) => {
    const notification = notifications.value.find(n => n.id === notificationId)
    const prev = notification?.read
    if (notification) notification.read = true
    try {
      const resp = await apiFetch(`/api/private/notifications/${notificationId}/read`, { method: 'PUT' })
      if (!resp.ok) throw new Error('read failed')
      return { success: true }
    } catch (err) {
      if (notification) notification.read = prev // rollback
      return { success: false, error: err.message }
    }
  }

  /**
   * Marque toutes les notifications comme lues (optimiste + API).
   */
  const markAllAsRead = async () => {
    const snapshot = notifications.value.map(n => n.read)
    notifications.value.forEach(n => { n.read = true })
    try {
      const resp = await apiFetch('/api/private/notifications/read-all', { method: 'PUT' })
      if (!resp.ok) throw new Error('read-all failed')
      return { success: true }
    } catch (err) {
      notifications.value.forEach((n, i) => { n.read = snapshot[i] }) // rollback
      return { success: false, error: err.message }
    }
  }

  /**
   * Supprime une notification (optimiste + API).
   */
  const removeNotification = async (notificationId) => {
    const idx = notifications.value.findIndex(n => n.id === notificationId)
    const removed = idx >= 0 ? notifications.value[idx] : null
    if (idx >= 0) notifications.value.splice(idx, 1)
    try {
      const resp = await apiFetch(`/api/private/notifications/${notificationId}`, { method: 'DELETE' })
      if (!resp.ok) throw new Error('delete failed')
      return { success: true }
    } catch (err) {
      if (removed) notifications.value.splice(idx, 0, removed) // rollback
      return { success: false, error: err.message }
    }
  }
  
  /**
   * Ajoute une notification localement
   */
  const addNotification = (notification) => {
    notifications.value.unshift({
      id: Date.now(),
      read: false,
      createdAt: new Date().toISOString(),
      ...notification,
    })
  }
  
  return {
    // State
    notifications,
    isLoading,
    error,
    
    // Getters
    unreadCount,
    unreadNotifications,
    readNotifications,
    
    // Actions
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    addNotification,
  }
})
